import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import { useAuthStore } from '@/shared/store/authStore'
import { isBookkeeper, runsTheStudio } from '@/shared/access/accessModel'

export interface StudioSettings {
  currency?: string
  studioName?: string
  [key: string]: unknown
}

/**
 * The studio settings this member is allowed to read.
 *
 * **Two endpoints, one cache entry, one shape.** An Owner, Admin or Bookkeeper reads
 * the whole of `GET /settings`. Everybody else reads `GET /settings/presentation` — the
 * studio's name, currency, locale, country and calendar timezone, and nothing else.
 * The field names are identical, so `settings?.currency` reads the same at every call
 * site and no caller has to know which one it got.
 *
 * **Why the branch exists (F70).** `GET /settings` is Owner/Admin/Finance, and this hook
 * used to call it unconditionally — so every Crew page load fired a request that 403'd
 * and left a console error, on pages that only ever wanted the currency to format an
 * amount with. Silencing the call would have been the other honest fix and the worse one:
 * the values are presentation, not secrets, and gating them is what produced the bug.
 * What a Crew member must not get is the rest of that payload — IBAN, SMTP credentials,
 * VAT number, storage quota — which is why the server answers from a separate, tiny
 * record rather than a redacted view of the big one.
 *
 * **The key is deliberately the same** `['settings']` for both. It means "whatever this
 * member can read of the studio settings", and it has to stay one entry: `formatters.ts`
 * reads the studio locale straight out of it, so a second entry for the same concept is
 * exactly the stale-formatting bug the key's own contract warns about. A member reads one
 * endpoint or the other, never both.
 */
export function useStudioSettings() {
  const { teamMember } = useAuthStore()
  const authorities = teamMember?.roles ?? []
  const readsFullSettings = runsTheStudio(authorities) || isBookkeeper(authorities)

  return useQuery<StudioSettings>({
    queryKey: queryKeys.settings.all,
    queryFn: async () => {
      const { data } = await apiClient.get(readsFullSettings ? '/settings' : '/settings/presentation')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}
